import type {ImageSource} from '@postermywall/fabricjs-2';

export interface ImageItemRender {
  loadedImageWidth: number;
  loadedImageHeight: number;
  /** The loaded image after refinement, crop, mask, edge effect and stroke border. */
  transformedImage: HTMLImageElement;
  /** What the item's fabric object draws: the transformed image, or a canvas with its colour filters already applied. */
  renderSource: ImageSource;
}

/**
 * Hands image items whose pixels come out the same one shared render. The render page uses it: an item there
 * changes its pixels only by reinitialising, which asks for the render of its new settings, so copies can draw the
 * same canvas. Without it a poster holding dozens of copies of one large upload builds a full-size filtered canvas
 * per copy and runs the generator out of memory.
 */
export class ImageItemRenderCache {
  readonly #renders = new Map<string, Promise<ImageItemRender>>();

  public getRender(key: string, buildRender: () => Promise<ImageItemRender>): Promise<ImageItemRender> {
    const cachedRender = this.#renders.get(key);
    if (cachedRender) {
      return cachedRender;
    }

    const render = buildRender();
    this.#renders.set(key, render);
    this.#forgetIfFailed(key, render);
    return render;
  }

  /** A failed load is not shared, so the next copy retries it the way an unshared item would. */
  #forgetIfFailed(key: string, render: Promise<ImageItemRender>): void {
    void render.catch((): void => {
      if (this.#renders.get(key) === render) {
        this.#renders.delete(key);
      }
    });
  }
}
